import { LINEAR_BASE_URL } from "./validationTicketUrl";

const INDY_TICKET_PROJECT = "Indy";
const INDY_TICKET_LABEL = "Indy";
// Linear priority: 0=None, 1=Urgent, 2=High, 3=Medium, 4=Low
const INDY_TICKET_PRIORITY_MEDIUM = "3";
const INDY_TICKET_STATUS = "Backlog";

const MAX_TITLE_TEXT_LENGTH = 80;
// Linear's new-issue URL is a GET; keep the whole thing well under browser
// URL limits even with a long reply quoted in the body.
const MAX_MESSAGE_LENGTH = 2500;
const MAX_DESCRIPTION_LENGTH = 4000;

/** Issue categories offered in the thumbs-down modal. Value = label in the ticket body. */
export enum IndyFeedbackCategory {
  IncorrectOrIncomplete = "Incorrect or incomplete",
  NotWhatIAskedFor = "Not what I asked for",
  SlowOrBuggy = "Slow or buggy",
  StyleOrTone = "Style or tone",
  SafetyOrLegalConcern = "Safety or legal concern",
  Other = "Other",
}

export const INDY_FEEDBACK_CATEGORIES: IndyFeedbackCategory[] =
  Object.values(IndyFeedbackCategory);

export interface IndyFeedbackTicketParams {
  category: IndyFeedbackCategory;
  feedback: string;
  conversationId: string;
  /** Absent for a reply that has not been persisted yet. */
  messageId: string | undefined;
  /** The assistant reply that was thumbed down, as plain markdown. */
  assistantMessage: string;
}

export function buildIndyFeedbackTicketUrl(
  params: IndyFeedbackTicketParams,
): string {
  const { category, feedback, conversationId, messageId, assistantMessage } =
    params;

  const trimmedFeedback = feedback.trim();
  const titleText = trimmedFeedback || "Thumbs down on assistant reply";
  const titleSnippet =
    titleText.length > MAX_TITLE_TEXT_LENGTH
      ? titleText.slice(0, MAX_TITLE_TEXT_LENGTH).trimEnd() + "…"
      : titleText;
  const title = `[Indy] Feedback: ${titleSnippet}`;

  const trimmedMessage = assistantMessage.trim();
  const quotedMessage =
    trimmedMessage.length > MAX_MESSAGE_LENGTH
      ? trimmedMessage.slice(0, MAX_MESSAGE_LENGTH) + "\n\n[truncated]"
      : trimmedMessage || "_(empty reply)_";

  const bodyLines: string[] = [
    "## Feedback\n",
    `- **Category**: ${category}`,
    "",
    trimmedFeedback || "_(no details provided)_",
    "",
    "## Context\n",
    `- **Conversation ID**: \`${conversationId}\``,
    `- **Message ID**: \`${messageId ?? "(not persisted)"}\``,
    "",
    "## Assistant message\n",
    quotedMessage,
  ];

  let body = bodyLines.join("\n");
  if (body.length > MAX_DESCRIPTION_LENGTH) {
    body = body.slice(0, MAX_DESCRIPTION_LENGTH) + "\n\n[truncated]";
  }

  const searchParams = new URLSearchParams({
    title,
    description: body,
    project: INDY_TICKET_PROJECT,
    labels: INDY_TICKET_LABEL,
    priority: INDY_TICKET_PRIORITY_MEDIUM,
    status: INDY_TICKET_STATUS,
  });

  return `${LINEAR_BASE_URL}?${searchParams.toString()}`;
}
